/**
 * Local authorization list (Phase I).
 *
 * - Lists entries from `GET /local-auth/:chargerId`.
 * - Surfaces the `local_list_version` vs `csms_list_version` diff prominently.
 * - "Force resync" button issues a `Full` push of the local table back to
 *   the charger.
 *
 * For Differential edits the FE builds a `_delete: true` per row; we keep
 * that out of v1 (read-mostly) and surface only the resync action so we
 * don't accidentally clobber local entries.
 */

import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import {
  getLocalAuthVersion,
  listLocalAuth,
  updateLocalAuth,
} from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { formatOcppTimestamp } from '../utils/statusFormat';
import type {
  ListLocalAuthResponse,
  LocalAuthEntry,
  LocalAuthVersionResponse,
} from '../models';
import type { OcppTabProps } from './types';

const LocalAuthListTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [entries, setEntries] = useState<LocalAuthEntry[]>([]);
  const [version, setVersion] = useState<LocalAuthVersionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, verRes] = await Promise.all([
      listLocalAuth(chargerId),
      getLocalAuthVersion(chargerId),
    ]);
    if (isOcppSuccess(listRes.status)) {
      const payload = listRes.payload as ListLocalAuthResponse | null;
      setEntries(payload?.entries ?? []);
      setError(null);
    } else {
      setError(mapOcppApiError(listRes.status, listRes.payload));
    }
    if (isOcppSuccess(verRes.status)) {
      setVersion(verRes.payload as LocalAuthVersionResponse | null);
    }
    setLoading(false);
  }, [chargerId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onForceResync = useCallback(async () => {
    setResyncing(true);
    setResyncResult(null);
    const res = await updateLocalAuth(chargerId, {
      update_type: 'Full',
      entries: entries.map((e) => ({
        id_tag: e.id_tag,
        parent_id_tag: e.parent_id_tag ?? undefined,
        status: e.status,
        expiry_date: e.expiry_date ?? undefined,
      })),
    });
    if (isOcppSuccess(res.status) && res.payload) {
      const updated = res.payload as { status: string; list_version?: number };
      setResyncResult(`Status: ${updated.status}${updated.list_version ? ` · v${updated.list_version}` : ''}`);
      await load();
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setResyncing(false);
  }, [chargerId, entries, load]);

  if (loading) return <OcppLoadingState label="Loading local authorization list…" />;

  const local = version?.list_version ?? null;
  const csms = version?.csms_list_version ?? null;
  const drifted = local !== null && csms !== null && local !== csms;

  return (
    <View>
      {error ? <OcppErrorBanner error={error} onRetry={load} /> : null}

      <OcppSectionCard
        title="List version"
        subtitle={
          drifted
            ? 'Local DB and CSMS versions diverge. Force a resync to bring them back in line.'
            : 'Local DB and CSMS agree on the active list version.'
        }
        trailing={
          <OcppStatusPill
            label={drifted ? 'Drift' : 'In sync'}
            color={drifted ? OcppTheme.warning : OcppTheme.success}
          />
        }
      >
        <View style={styles.versionRow}>
          <Text style={styles.versionLabel}>Backend</Text>
          <Text style={styles.versionValue}>{local ?? '—'}</Text>
        </View>
        <View style={styles.versionRow}>
          <Text style={styles.versionLabel}>CSMS</Text>
          <Text style={styles.versionValue}>{csms ?? '—'}</Text>
        </View>
        <OcppActionButton
          label="Force full resync"
          icon="sync"
          onPress={onForceResync}
          loading={resyncing}
          variant={drifted ? 'primary' : 'secondary'}
          style={{ alignSelf: 'flex-start', marginTop: 12 }}
        />
        {resyncResult ? <Text style={styles.resyncResult}>{resyncResult}</Text> : null}
      </OcppSectionCard>

      <OcppSectionCard title="Entries" subtitle={`${entries.length} id tag(s) cached on the charger.`}>
        {entries.length === 0 ? (
          <OcppEmptyState
            icon="badge"
            title="No id tags cached"
            description="The charger has not been pushed any local authorization entries."
          />
        ) : (
          entries.map((entry) => (
            <View key={entry.id} style={styles.entryRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.entryTag}>{entry.id_tag}</Text>
                <Text style={styles.entryMeta}>
                  v{entry.list_version}
                  {entry.parent_id_tag ? ` · group ${entry.parent_id_tag}` : ''}
                  {entry.expiry_date ? ` · expires ${formatOcppTimestamp(entry.expiry_date)}` : ''}
                </Text>
              </View>
              <OcppStatusPill
                label={entry.status}
                color={entry.status === 'Accepted' ? OcppTheme.success : OcppTheme.warning}
                size="sm"
              />
            </View>
          ))
        )}
      </OcppSectionCard>
    </View>
  );
};

export default LocalAuthListTab;

const styles = StyleSheet.create({
  versionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  versionLabel: {
    color: OcppTheme.textMuted,
    fontSize: 13,
  },
  versionValue: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '700',
  },
  resyncResult: {
    color: OcppTheme.success,
    fontSize: 12,
    marginTop: 8,
  },
  entryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  entryTag: {
    color: OcppTheme.textPrimary,
    fontSize: 14,
    fontWeight: '600',
  },
  entryMeta: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginTop: 2,
  },
});
